import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card, Col, Row, Container } from 'react-bootstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from "../../api/axiosConfig.jsx";

const TimeslotManager = () => {
    const [openingHours, setOpeningHours] = useState([]);
    const [timeslots, setTimeslots] = useState([]);
    const [selectedTimeslot, setSelectedTimeslot] = useState(null);
    const [showTimeslotModal, setShowTimeslotModal] = useState(false);
    const [showOpeningHoursModal, setShowOpeningHoursModal] = useState(false);
    const [selectedOpeningHours, setSelectedOpeningHours] = useState(null);
    const [reason, setReason] = useState('');
    const [date, setDate] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchOpeningHours();
        fetchTimeslots();
    }, []);

    const fetchOpeningHours = async () => {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            setError('Geen authenticatie token gevonden. Log opnieuw in.');
            return;
        }

        try {
            const decodedToken = jwtDecode(authToken);
            const companyId = decodedToken.companyId;

            const response = await axiosInstance.get(`/opening-hours/company/${companyId}`)
            setOpeningHours(response.data);
        } catch (error) {
            console.error('Error fetching opening hours:', error);
            setError('Er is een fout opgetreden bij het ophalen van de openingstijden. Probeer het later opnieuw.');
        }
    };

    const colors = {
        primary: '#2c3e50',
        secondary: '#34495e',
        accent: '#3498db',
        success: '#27ae60',
        danger: '#e74c3c',
        light: '#ffffff',
        dark: '#2c3e50',
        text: '#333333',
    };


    const fetchTimeslots = async () => {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            setError('Geen authenticatie token gevonden. Log opnieuw in.');
            return;
        }

        try {
            const decodedToken = jwtDecode(authToken);
            const companyId = decodedToken.companyId;

            const response = await axiosInstance.get(`/timeslots/company/${companyId}`, {
                headers: {Authorization: `Bearer ${authToken}`}
            });
            setTimeslots(response.data);
        } catch (error) {
            console.error('Error fetching timeslots:', error);
            setError('Er is een fout opgetreden bij het ophalen van de tijdsloten. Probeer het later opnieuw.');
        }
    };

    const handleTimeslotClick = (timeslot) => {
        setSelectedTimeslot(timeslot);
        setShowTimeslotModal(true);
        setError(null);
        setDate('');
        setReason('');
    };

    const handleOpeningHoursClick = (openingHours) => {
        setSelectedOpeningHours(openingHours);
        setShowOpeningHoursModal(true);
        setError(null);
    };

    const handleCloseTimeslot = async () => {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            setError('Geen authenticatie token gevonden. Log opnieuw in.');
            return;
        }

        try {
            const decodedToken = jwtDecode(authToken);
            const companyId = decodedToken.companyId;

            const response = await axiosInstance.post('/reservation-timeslots/close', {
                companyId: companyId,
                reservationTimeslotId: selectedTimeslot.timeslotId,
                date: date,
                reason: reason
            })
            if (response.status === 200) {
                // Tijdslot succesvol gesloten
                setShowTimeslotModal(false);
                fetchTimeslots(); // Ververs de tijdsloten
                setError(null);
            } else {
                setError('Er is een fout opgetreden bij het sluiten van het tijdslot.');
            }
        } catch (error) {
            console.error('Error closing timeslot:', error);
            setError('Er is een fout opgetreden bij het sluiten van het tijdslot. Probeer het later opnieuw.');
        }
    };

    const handleUpdateOpeningHours = async () => {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) {
            setError('Geen authenticatie token gevonden. Log opnieuw in.');
            return;
        }

        try {
            const response = await axiosInstance.put(`/opening-hours/${selectedOpeningHours.id}`, {
                isOpen: selectedOpeningHours.isOpen,
                openTime: selectedOpeningHours.openTime,
                closeTime: selectedOpeningHours.closeTime,
                dayOfWeek: selectedOpeningHours.dayOfWeek
            })

            setOpeningHours(openingHours.map(oh => oh.id === response.data.id ? response.data : oh));
            setShowOpeningHoursModal(false);
        } catch (error) {
            console.error('Error updating opening hours:', error);
            setError('Er is een fout opgetreden bij het bijwerken van de openingstijden. Probeer het later opnieuw.');
        }
    };

    const getDayName = (dayNumber) => {
        const days = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
        return days[dayNumber - 1];
    };

    const formatTime = (time) => {
        return time.slice(0, 5);
    };

    const adjustTimeForOvernight = (day, openTime, closeTime) => {
        if ((day === 5 || day === 6) && closeTime === "04:00:00") {
            return `${formatTime(openTime)} - 04:00 (volgende dag)`;
        }
        return `${formatTime(openTime)} - ${formatTime(closeTime)}`;
    };

    const groupTimeslotsByHour = (timeslots) => {
        const grouped = {};
        timeslots.forEach(timeslot => {
            const hour = parseInt(timeslot.startTime.split(':')[0], 10);
            if (hour >= 1 && hour <= 23) {
                if (!grouped[hour]) {
                    grouped[hour] = [];
                }
                grouped[hour].push(timeslot);
            }
        });
        return grouped;
    };

    const groupedTimeslots = groupTimeslotsByHour(timeslots);


    return (
        <Container fluid className="mt-5">
            <Row>
                <Col md={4}>
                    <h2 style={{color: colors.primary, marginBottom: '20px'}}>Openingstijden</h2>
                    <div style={{height: '70vh', overflowY: 'auto'}}>
                        {openingHours.sort((a, b) => a.dayOfWeek - b.dayOfWeek).map((day) => (
                            <Card
                                key={day.id}
                                className="mb-2"
                                onClick={() => handleOpeningHoursClick(day)}
                                style={{
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    border: 'none',
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                            >
                                <Card.Body
                                    className="text-center"
                                    style={{
                                        borderRadius: '8px',
                                        backgroundColor: day.isOpen ? colors.success : colors.danger,
                                        color: colors.light,
                                    }}
                                >
                                    <Card.Title style={{color: colors.light}}>{getDayName(day.dayOfWeek)}</Card.Title>
                                    <Card.Text>
                                        {day.isOpen ? adjustTimeForOvernight(day.dayOfWeek, day.openTime, day.closeTime) : 'Gesloten'}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                </Col>
                <Col md={8}>
                    <h2 style={{color: colors.primary, marginBottom: '20px'}}>Tijdsloten</h2>
                    <div style={{height: '70vh', overflowY: 'auto'}}>
                        {Object.entries(groupedTimeslots).map(([hour, slots]) => (
                            <div key={hour} className="mb-4">
                                <h3 style={{color: colors.secondary}}>{hour}:00</h3>
                                <Row>
                                    {slots.map((timeslot) => (
                                        <Col md={3} key={timeslot.timeslotId} className="mb-2">
                                            <Card
                                                onClick={() => handleTimeslotClick(timeslot)}
                                                style={{
                                                    cursor: 'pointer',
                                                    height: '80px',
                                                    borderRadius: '8px',
                                                    transition: 'all 0.2s',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    border: 'none',
                                                    backgroundColor: timeslot.isOpen ? colors.accent : colors.light,
                                                    color: timeslot.isOpen ? colors.light : colors.text,
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'}
                                                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)'}
                                            >
                                                <Card.Body className="d-flex flex-column justify-content-center">
                                                    <Card.Title style={{color: timeslot.isOpen ? colors.light : colors.text}}>
                                                        {formatTime(timeslot.startTime)} - {formatTime(timeslot.endTime)}
                                                    </Card.Title>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>

            {/* Timeslot Modal */}
            <Modal show={showTimeslotModal} onHide={() => setShowTimeslotModal(false)} centered>
                <Modal.Header closeButton style={{backgroundColor: colors.primary, color: colors.light}}>
                    <Modal.Title>Tijdslot Sluiten</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p>Geselecteerd tijdslot: {selectedTimeslot ? `${formatTime(selectedTimeslot.startTime)} - ${formatTime(selectedTimeslot.endTime)}` : ''}</p>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Datum</Form.Label>
                            <Form.Control
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Reden (optioneel)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTimeslotModal(false)}>
                        Annuleren
                    </Button>
                    <Button variant="primary" onClick={handleCloseTimeslot} style={{backgroundColor: colors.accent, borderColor: colors.accent}}>
                        Tijdslot Sluiten
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Opening Hours Modal */}
            <Modal show={showOpeningHoursModal} onHide={() => setShowOpeningHoursModal(false)} centered>
                <Modal.Header closeButton style={{backgroundColor: colors.primary, color: colors.light}}>
                    <Modal.Title>Openingstijden Aanpassen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {selectedOpeningHours && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="checkbox"
                                    label="Open"
                                    checked={selectedOpeningHours.isOpen}
                                    onChange={() => setSelectedOpeningHours({
                                        ...selectedOpeningHours,
                                        isOpen: !selectedOpeningHours.isOpen
                                    })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Openingstijd</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={formatTime(selectedOpeningHours.openTime)}
                                    onChange={(e) => setSelectedOpeningHours({
                                        ...selectedOpeningHours,
                                        openTime: e.target.value
                                    })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Sluitingstijd</Form.Label>
                                <Form.Control
                                    type="time"
                                    value={formatTime(selectedOpeningHours.closeTime)}
                                    onChange={(e) => setSelectedOpeningHours({
                                        ...selectedOpeningHours,
                                        closeTime: e.target.value
                                    })}
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOpeningHoursModal(false)}>
                        Annuleren
                    </Button>
                    <Button variant="primary" onClick={handleUpdateOpeningHours} style={{backgroundColor: colors.accent, borderColor: colors.accent}}>
                        Bijwerken
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

    export default TimeslotManager;
